/**
 * @description Time formatting utilities for the time clock portal
 *
 * Key features:
 * - Duration formatting (minutes → "Xh Ym")
 * - Time formatting (ISO → "9:00 AM")
 * - Date formatting (ISO → "Mon, Jan 24")
 * - Shift time range formatting
 * - Countdown calculation
 * - Group entries by date
 */

/**
 * Format minutes into human-readable duration
 * @param {number} minutes
 * @returns {string} "8h 30m" or "45m"
 */
export const formatDuration = (minutes) => {
  if (!minutes || minutes <= 0) return '0m';
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

/**
 * Format ISO timestamp to time string
 * @param {string} isoString
 * @returns {string} "9:00 AM"
 */
export const formatTime = (isoString) => {
  if (!isoString) return '';
  return new Date(isoString).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Format ISO timestamp to date string
 * @param {string} isoString
 * @returns {string} "Mon, Jan 24"
 */
export const formatDate = (isoString) => {
  if (!isoString) return '';
  // Calendar-date prefix — same as dashboard parseDateSafely / timeEntryDisplay
  // so full-day PTO stored as UTC midnight does not shift to the previous local day.
  const raw = String(isoString);
  const [year, month, day] = raw.slice(0, 10).split('-').map(Number);
  if (year && month && day) {
    return new Date(year, month - 1, day).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  }
  return new Date(isoString).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

export const isPtoEntry = (entry) =>
  entry?.entryKind === 'pto' || entry?.entry_kind === 'pto';

/**
 * Format a shift time range
 * @param {string} startTime - ISO start
 * @param {string} endTime - ISO end
 * @returns {string} "Mon, Jan 24 · 9:00 AM — 5:00 PM"
 */
export const formatShiftTime = (startTime, endTime) => {
  if (!startTime) return 'Unscheduled';
  const date = formatDate(startTime);
  const start = formatTime(startTime);
  const end = endTime ? formatTime(endTime) : 'TBD';
  return `${date} · ${start} — ${end}`;
};

/**
 * Calculate countdown to a future time
 * @param {string} targetTime - ISO timestamp
 * @returns {string|null} "2h 15m until your shift" or null if past
 */
export const getCountdown = (targetTime) => {
  if (!targetTime) return null;
  const target = new Date(targetTime).getTime();
  const now = Date.now();
  const diff = target - now;

  if (diff <= 0) return null;

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return `${days} day${days !== 1 ? 's' : ''} until your shift`;
  }

  if (hours === 0) return `${minutes}m until your shift`;
  return `${hours}h ${minutes}m until your shift`;
};

/**
 * Group time entries by date
 * @param {Array} entries - Array of time entry objects
 * @returns {Object} { "Mon, Jan 24": [...entries], ... }
 */
export const groupByDate = (entries) => {
  const groups = {};
  entries.forEach((entry) => {
    const date = formatDate(entry.clockInAt);
    if (!groups[date]) groups[date] = [];
    groups[date].push(entry);
  });
  return groups;
};
